import React, { useState, useEffect } from 'react';
import _, { isArray } from 'lodash';
//ui
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { withStyles, CircularProgress } from '@material-ui/core';

//api
import { SupplierResponse, GetSuppliersParams } from 'lib/api/supplierData';
import { DetectedSupplier, DetectedClient } from 'lib/api/detectInvoice';
import { GetClientsParams, ClientResponse } from 'lib/api/clientData';
import { useIntl } from 'react-intl';
import { useFormContext } from '@domonda/react-form';
import { FormValues } from 'modules/InvoiceDetail/InvoiceDetail/selectors';

export interface InvoiceSupplierAutocompleteProps {
  onValueChange: (value: DetectedSupplier | DetectedClient | null) => void;
  tabIndex?: number | undefined;
  columnName: string;
  value: string;
  fieldName: string;
  isSupplier: boolean;
  minCharacters: number;
  getData: (
    params: GetSuppliersParams | GetClientsParams,
  ) => Promise<SupplierResponse | ClientResponse>;
  label: string;
  clientId: string;
  onFocus: () => void;
  autoFocus: boolean | undefined;
}

const CssTextField = withStyles({
  root: {
    '& .MuiFilledInput-root': {
      background: 'transparent',
    },
    '& .MuiFilledInput-underline:before': {
      border: 'none'
    },
    '& .MuiFilledInput-underline:after': {
      border: 'none'
    },
    '& label.Mui-focused': {
      color: 'rgb(59, 59, 59)',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: 'rgb(59, 59, 59);',
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        border: '1px solid rgb(59, 59, 59);',
      },
      '&.Mui-focused': {
        background: 'white',
      },
    },
  },
})(TextField);

const InvoiceAutocomplete: React.FC<InvoiceSupplierAutocompleteProps> = (props) => {
  const {
    onValueChange,
    columnName,
    autoFocus,
    clientId,
    minCharacters,
    getData,
    isSupplier,
    onFocus,
    label,
    fieldName,
    tabIndex,
    value,
  } = props;
  const [options, setOptions] = useState<SupplierResponse | ClientResponse>();
  const [resultOptions, setResultOptions] = useState<SupplierResponse | undefined>(undefined);
  const [, setInputText] = useState('');
  const [val, setVal] = useState({});
  const [selected, setSelected] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [newClientId, setNewClientId] = useState('');
  const [matchingValueIds,setMatchingValueIds] = useState<string[]>([]);
  const { formatMessage } = useIntl();

  const form = useFormContext<FormValues>();

  useEffect(() => {
    setReadOnly(isSupplier && !form.values.Invoice.DetectInvoiceResponse.Client!.Id);
    if (form.values.Invoice.DetectInvoiceResponse.Client!.Id)
      setNewClientId(form.values.Invoice.DetectInvoiceResponse.Client!.Id);
  }, [form.values.Invoice.DetectInvoiceResponse.Client!.Id]);

  const findOption = (selected: DetectedSupplier) => {
    if (!selected) return null;
    let foundValue = null;
    resultOptions!.Suppliers.forEach((supplier) => {
      if (supplier.SupplierId === selected.SupplierId) {
        foundValue = supplier;
      }
    });
    if (foundValue) {
      while ((foundValue as any).Ibans.length > 2) (foundValue as any).Ibans.pop();
      while ((foundValue as any).Uids.length > 2) (foundValue as any).Uids.pop();
    }
    return foundValue;
  };

  useEffect(() => {
    setVal(value);
  }, [value]);

  async function fetchData(text: string) {
    if (text.length >= minCharacters && !selected) {
      setLoading(true);
      const response = isSupplier
        ? await getData({
            searchText: text,
            columnName,
            ClientId: newClientId ? newClientId : clientId,
          })
        : await getData({
            searchText: text,
            columnName: columnName === 'Id' ? 'clientId' : columnName,
          });
      const newOptions: any[] = [];
      if (columnName === 'Ibans' || columnName === 'Uids') {
        (response as any).Suppliers.forEach((supplier: any) => {
          supplier[columnName].forEach((value: any) => {
            newOptions.push({ ...supplier, [columnName]: value });
          });
        });
        setResultOptions(response as any);
        setOptions({ Message: '', State: 0, Suppliers: newOptions });
      } else setOptions(response);
      if (isSupplier) {
        if (newOptions.length > 0) {
          const matchingIds: string[] = [];
          for (let i = 0; i < newOptions.length - 1; i++) {
            const element = newOptions[i][columnName];
            for (let j = i + 1; j < newOptions.length; j++) {
              if (element === newOptions[j][columnName]) {
                matchingIds[newOptions[i].SupplierId] = newOptions[i].SupplierId;
                matchingIds[newOptions[j].SupplierId] = newOptions[j].SupplierId;
              }
            }
          }
          setMatchingValueIds(matchingIds);
        } else {
          const matchingIds: string[] = [];
          const responseSuppliers = ((response as unknown) as SupplierResponse).Suppliers;
          for (let i = 0; i < responseSuppliers.length - 1; i++) {
            for (let j = i + 1; j < responseSuppliers.length; j++) {
              if ((responseSuppliers as any)[i][columnName] === (responseSuppliers as any)[j][columnName]) {
                matchingIds[(responseSuppliers as any)[i].SupplierId] = responseSuppliers[i].SupplierId;
                matchingIds[(responseSuppliers as any)[j].SupplierId] = responseSuppliers[j].SupplierId;
              }
            }
          }
          setMatchingValueIds(matchingIds);
        }
      }
      setLoading(false);
    }
    setSelected(false);
  }

  const getBackgroundColor = ()=> {
    if(isSupplier && form.values.Invoice.DetectInvoiceResponse.Supplier!.SupplierId === " ") return 'rgba(255, 0, 0, 0.1)';
    if(isSupplier && form.values.Invoice.DetectInvoiceResponse.Supplier!.SupplierId) return 'rgb(243, 255, 229)';
    if(isSupplier && !form.values.Invoice.DetectInvoiceResponse.Supplier!.SupplierId) return 'rgba(255, 0, 0, 0.1)';
    if(!isSupplier && form.values.Invoice.DetectInvoiceResponse.Client!.Id) return 'rgb(243, 255, 229)';
    if(!isSupplier && !form.values.Invoice.DetectInvoiceResponse.Client!.Id) return 'rgba(255, 0, 0, 0.1)';
  }
  const getBorderColor = ()=> {
    if(isSupplier && form.values.Invoice.DetectInvoiceResponse.Supplier!.SupplierId === " ") return 'red';
    if(isSupplier && form.values.Invoice.DetectInvoiceResponse.Supplier!.SupplierId) return 'rgb(146, 208, 80)';
    if(isSupplier && !form.values.Invoice.DetectInvoiceResponse.Supplier!.SupplierId) return 'red';
    if(!isSupplier && form.values.Invoice.DetectInvoiceResponse.Client!.Id) return 'rgb(146, 208, 80)';
    if(!isSupplier && !form.values.Invoice.DetectInvoiceResponse.Client!.Id) return 'red';
  }

  return (
    <Autocomplete
      value={val}
      open={open}
      disabled={readOnly}
      onBlur={() => {
        setOptions(undefined);
        setResultOptions(undefined);
      }}
      onFocus={onFocus}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      loadingText={formatMessage({ id: 'AUTOCOMPLETE_LOADING' })}
      noOptionsText={formatMessage({ id: 'INVOICE_DETAIL.NO_RESULTS' })}
      loading={loading}
      options={
        options
          ? (options as any).Suppliers
            ? (options as any).Suppliers
            : (options as any).Clients
          : []
      }
      getOptionLabel={(option) => {
        // const condition = columnName==="City" || columnName==="Country" || columnName==="ZipCode";
        // const columnOption = (option as any)[condition?"Name":columnName];
        const columnOption = (option as any)[columnName];
        if (isArray(columnOption)) return _.get(option, fieldName);
        return columnOption ? columnOption : '';
      }}
      renderOption={(option, _state) => {
        const columnOption = (option as any)[columnName];
        if (isArray(columnOption)) return _.get(option, fieldName);
        let returnOption = ''
        if(isSupplier && matchingValueIds[(option as any).SupplierId]) returnOption = columnOption? `${columnOption} (${(option as any).Name})`:'';
        else returnOption = columnOption?columnOption:'';
        return returnOption;
      }}

      style={{ width: '100%'}}
      autoComplete
      onInputChange={(_event, newInputValue, reason) => {
        setInputText(newInputValue);
        if (reason === 'input') fetchData(newInputValue);
      }}
      onChange={(_event, newValue, _reason) => {
        setSelected(true);
        if (columnName === 'Ibans' || columnName === 'Uids'){
          onValueChange(findOption(newValue as any));
        }
        else {
          if (newValue && (newValue as any).SupplierId) {
            while ((newValue as any).Ibans.length > 2) (newValue as any).Ibans.pop();
            while ((newValue as any).Uids.length > 2) (newValue as any).Uids.pop();
          }
          onValueChange(newValue as any);
        }
      }}
      getOptionSelected={(option, value) => {
        return (option as any)[columnName] === (value as any)[columnName];
      }}
      renderInput={(params) => (
        <CssTextField
          {...params}
          id="supplier-autocomplete"
          variant="filled"
          label={label}
          autoFocus={autoFocus}
          inputProps={{
            ...params.inputProps,
            tabIndex,
          }}
          style={{
            backgroundColor: getBackgroundColor(),
            border:`1px solid ${getBorderColor()}` ,
            borderRadius: '5px'
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export { InvoiceAutocomplete };
